import type {
  TripView,
  CreateTripResponse,
  PoiView,
  AutocompleteSuggestion,
  AutocompleteResponse,
  BookmarkResponse,
  GroupView,
  GroupTint,
  PlacePreview,
  PlacePreviewResponse,
  ResolveUrlResponse,
  TripCityView,
  ListCitiesResponse,
  AddCityResponse,
  CityAutocompleteSuggestion,
  CityAutocompleteResponse,
  TextSearchResult,
  TextSearchResponse,
  ViewportRect,
  UserDashboardView,
  SaveToDashboardResponse,
} from '~/shared/api.js';

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new Error(await safeError(res));
  return res.json() as Promise<T>;
}

async function safeError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? `Request failed: ${res.status}`;
  } catch {
    return `Request failed: ${res.status}`;
  }
}

/**
 * Module-level language code for Places API requests. Set by the
 * trip page after resolving the active trip's map_label_mode. Used by
 * every Places-touching API call to localize responses. The language
 * is constant for the page's lifetime — toggling the mode reloads the
 * page, which re-runs the resolver against the new trip state.
 */
let placesLanguage: string | null = null;
export function setPlacesLanguage(lang: string | null): void {
  placesLanguage = lang;
}
function withLang<T extends Record<string, unknown>>(body: T): T & { languageCode?: string } {
  return placesLanguage ? { ...body, languageCode: placesLanguage } : body;
}

export async function createTrip(): Promise<CreateTripResponse> {
  return apiJson('/api/trips', { method: 'POST' });
}

export async function fetchTrip(token: string): Promise<TripView | null> {
  const res = await fetch(`/api/trips/${token}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await safeError(res));
  return res.json();
}

export async function updateTrip(
  token: string,
  patch: {
    name?: string;
    linkEditEnabled?: boolean;
    coverPoiId?: number | null;
    mapLabelMode?: 'local' | 'mine';
  },
): Promise<TripView> {
  return apiJson<TripView>(`/api/trips/${token}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function deleteTrip(token: string): Promise<void> {
  const res = await fetch(`/api/trips/${token}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function fetchPois(token: string): Promise<PoiView[]> {
  const body = await apiJson<{ pois: PoiView[] }>(`/api/trips/${token}/pois`);
  return body.pois;
}

export async function autocompletePlaces(
  token: string,
  query: string,
  sessionToken: string,
  options: { viewport?: ViewportRect | null; signal?: AbortSignal } = {},
): Promise<AutocompleteSuggestion[]> {
  const body = await apiJson<AutocompleteResponse>(`/api/trips/${token}/autocomplete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      withLang({ query, sessionToken, viewport: options.viewport ?? undefined }),
    ),
    signal: options.signal,
  });
  return body.suggestions;
}

export async function fetchPlacePreview(
  token: string,
  placeId: string,
  sessionToken: string,
): Promise<PlacePreview> {
  const body = await apiJson<PlacePreviewResponse>(
    `/api/trips/${token}/place-preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withLang({ placeId, sessionToken })),
    },
  );
  return body.preview;
}

export async function resolvePlaceUrl(
  token: string,
  url: string,
): Promise<ResolveUrlResponse> {
  return apiJson<ResolveUrlResponse>(`/api/trips/${token}/resolve-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withLang({ url })),
  });
}

export async function bookmarkPlace(
  token: string,
  placeId: string,
  sessionToken: string,
  displayName?: string,
): Promise<BookmarkResponse> {
  return apiJson<BookmarkResponse>(`/api/trips/${token}/pois`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withLang({ placeId, sessionToken, displayName })),
  });
}

export async function deletePoi(token: string, poiId: number): Promise<void> {
  const res = await fetch(`/api/trips/${token}/pois/${poiId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function updatePoi(
  token: string,
  poiId: number,
  patch: { customName?: string | null; notes?: string | null; cityId?: number | null },
  signal?: AbortSignal,
): Promise<PoiView> {
  const body = await apiJson<{ poi: PoiView }>(`/api/trips/${token}/pois/${poiId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
    signal,
  });
  return body.poi;
}

export async function refreshPoiDetails(
  token: string,
  poiId: number,
  signal?: AbortSignal,
): Promise<PoiView> {
  const body = await apiJson<{ poi: PoiView }>(
    `/api/trips/${token}/pois/${poiId}/refresh-details`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withLang({})),
      signal,
    },
  );
  return body.poi;
}

export async function fetchGroups(token: string): Promise<GroupView[]> {
  const body = await apiJson<{ groups: GroupView[] }>(`/api/trips/${token}/groups`);
  return body.groups;
}

export async function createGroup(
  token: string,
  name: string,
  tint: GroupTint,
): Promise<GroupView> {
  const body = await apiJson<{ group: GroupView }>(`/api/trips/${token}/groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, tint }),
  });
  return body.group;
}

export async function deleteGroup(token: string, groupId: number): Promise<void> {
  const res = await fetch(`/api/trips/${token}/groups/${groupId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function reorderGroups(token: string, ids: number[]): Promise<void> {
  const res = await fetch(`/api/trips/${token}/groups/order`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function reorderCities(token: string, ids: number[]): Promise<void> {
  const res = await fetch(`/api/trips/${token}/cities/order`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function updateGroup(
  token: string,
  groupId: number,
  changes: { name?: string; tint?: GroupTint },
): Promise<GroupView> {
  const body = await apiJson<{ group: GroupView }>(`/api/trips/${token}/groups/${groupId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  return body.group;
}

export async function setPoiGroupMembership(
  token: string,
  poiId: number,
  groupIds: number[],
): Promise<PoiView> {
  const body = await apiJson<{ poi: PoiView }>(
    `/api/trips/${token}/pois/${poiId}/group-membership`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupIds }),
    },
  );
  return body.poi;
}

export async function fetchCities(token: string): Promise<TripCityView[]> {
  const body = await apiJson<ListCitiesResponse>(`/api/trips/${token}/cities`);
  return body.cities;
}

export async function autocompleteCities(
  token: string,
  query: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<CityAutocompleteSuggestion[]> {
  const body = await apiJson<CityAutocompleteResponse>(`/api/trips/${token}/cities/autocomplete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withLang({ query, sessionToken })),
    signal,
  });
  return body.suggestions;
}

export async function addCity(
  token: string,
  placeId: string,
  sessionToken: string,
  setActive = true,
): Promise<AddCityResponse> {
  return apiJson<AddCityResponse>(`/api/trips/${token}/cities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withLang({ placeId, sessionToken, setActive })),
  });
}

export async function deleteCity(token: string, cityId: number): Promise<void> {
  const res = await fetch(`/api/trips/${token}/cities/${cityId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function setActiveCity(token: string, cityId: number | null): Promise<void> {
  await apiJson(`/api/trips/${token}/active-city`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cityId }),
  });
}

export async function textSearch(
  token: string,
  query: string,
  options: {
    viewport?: ViewportRect | null;
    /** True = restrict results to the viewport (hard filter), used by
     *  discovery mode. False/omitted = bias toward the viewport. */
    restrict?: boolean;
    /** Min rating filter (0–5) — Places API native filter. */
    minRating?: number;
    /** Restrict to currently-open places — Places API native filter. */
    openNow?: boolean;
    signal?: AbortSignal;
  } = {},
): Promise<TextSearchResult[]> {
  const body = await apiJson<TextSearchResponse>(`/api/trips/${token}/text-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      withLang({
        query,
        viewport: options.viewport ?? undefined,
        restrict: options.restrict ?? undefined,
        minRating: options.minRating ?? undefined,
        openNow: options.openNow ?? undefined,
      }),
    ),
    signal: options.signal,
  });
  return body.results;
}

export function photoUrl(photoReference: string | null, maxWidth = 720): string | null {
  if (!photoReference) return null;
  return `/api/photo?ref=${encodeURIComponent(photoReference)}&w=${maxWidth}`;
}

export function newSessionToken(): string {
  return crypto.randomUUID();
}

export async function authRequestCode(email: string): Promise<void> {
  await apiJson<{ ok: true }>('/api/auth/request-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export async function authVerifyCode(email: string, code: string): Promise<void> {
  await apiJson<{ ok: true }>('/api/auth/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
}

export async function authSignOut(): Promise<void> {
  await apiJson<{ ok: true }>('/api/auth/sign-out', { method: 'POST' });
}

export async function authDeleteAccount(): Promise<void> {
  const res = await fetch('/api/auth/account', { method: 'DELETE' });
  if (!res.ok) throw new Error(await safeError(res));
}

export async function fetchUserDashboard(token: string): Promise<UserDashboardView | null> {
  const res = await fetch(`/api/users/${token}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await safeError(res));
  return res.json();
}

export async function saveTripToDashboard(tripToken: string): Promise<SaveToDashboardResponse> {
  return apiJson<SaveToDashboardResponse>(`/api/trips/${tripToken}/save-to-dashboard`, {
    method: 'POST',
  });
}

export async function forkTrip(tripToken: string): Promise<CreateTripResponse> {
  return apiJson<CreateTripResponse>(`/api/trips/${tripToken}/fork`, { method: 'POST' });
}
